import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, FileDown, Receipt, PenLine } from "lucide-react";
import { api, download, dt } from "@/lib/api";
import { useAuth, canWrite, isAgent } from "@/context/AuthContext";
import { MailButton } from "@/components/MailButton";
import { SignatureDialog } from "@/components/SignatureDialog";
import { PageHeader, Empty } from "@/components/Common";
import { ConfirmDelete } from "@/components/ConfirmDelete";
import { PrintButton } from "@/components/PrintButton";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const name = (p) => p?.denominazione || `${p?.nome || ""} ${p?.cognome || ""}`;

const Stato = ({ d }) => (
  <div className="flex flex-wrap gap-1">
    {d.invoice_id
      ? <Link to={`/app/fatture/${d.invoice_id}`} data-testid={`ddt-invoiced-${d.id}`} className="rounded bg-emerald-50 px-2 py-0.5 text-xs text-emerald-700 underline">Fatturato · {d.invoice_numero}</Link>
      : <span className="rounded bg-amber-50 px-2 py-0.5 text-xs text-amber-700">Da fatturare</span>}
    {d.firma && <span data-testid={`ddt-signed-${d.id}`} title={`Firmato da ${d.firma.nome}`} className="rounded bg-sky-50 px-2 py-0.5 text-xs text-sky-700">Firmato</span>}
  </div>
);

export default function DdtList() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState(null);
  const [firma, setFirma] = useState(null);
  const load = useCallback(() => api.get("/ddt").then((r) => setItems(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const pdf = (d) => download(`/ddt/${d.id}/pdf`).catch((e) => toast.error(e.message));
  const del = async (id) => {
    await api.delete(`/ddt/${id}`);
    toast.success("DDT eliminato");
    load();
  };
  return (
    <div>
      <PageHeader title="Documenti di trasporto" subtitle="Compila i DDT e inseriscili poi in fattura (fattura differita TD24)">
        <PrintButton testid="print-ddt-button" path="/stampe/ddt.pdf" />
        {canWrite(user) && <Button data-testid="new-ddt-button" className="bg-sky-600 hover:bg-sky-700" onClick={() => nav("/app/ddt/nuovo")}><Plus className="mr-2 h-4 w-4" />Nuovo DDT</Button>}
      </PageHeader>
      {items && items.length === 0 ? <Empty text="Nessun documento di trasporto" /> : (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
          <Table data-testid="ddt-table">
            <TableHeader><TableRow className="bg-slate-50">
              <TableHead>Numero</TableHead><TableHead>Data</TableHead><TableHead>Destinatario</TableHead><TableHead>Causale</TableHead><TableHead>Stato</TableHead><TableHead />
            </TableRow></TableHeader>
            <TableBody>
              {(items || []).map((d) => (
                <TableRow key={d.id} data-testid={`ddt-row-${d.id}`}>
                  <TableCell className="font-mono">{d.numero}</TableCell>
                  <TableCell>{dt(d.data)}</TableCell>
                  <TableCell className="font-medium">{name(d.destinatario)}</TableCell>
                  <TableCell className="text-sm">{d.causale_trasporto}</TableCell>
                  <TableCell><Stato d={d} /></TableCell>
                  <TableCell className="whitespace-nowrap text-right">
                    <Button variant="ghost" size="icon" title="PDF" data-testid={`ddt-pdf-${d.id}`} onClick={() => pdf(d)}><FileDown className="h-4 w-4" /></Button>
                    {canWrite(user) && <Button variant="ghost" size="sm" title="Firma del cliente alla consegna" data-testid={`ddt-sign-${d.id}`} onClick={() => setFirma(d)}><PenLine className="mr-1 h-4 w-4" />{d.firma ? "Rifirma" : "Firma"}</Button>}
                    {canWrite(user) && <MailButton icon path={`/ddt/${d.id}/email`} email={d.destinatario?.email} what="il DDT di consegna" testid={`ddt-email-${d.id}`} />}
                    {canWrite(user) && !d.invoice_id && <>
                      <Button variant="ghost" size="sm" data-testid={`ddt-invoice-${d.id}`} onClick={() => nav(`/app/fatture/nuova?ddt=${d.id}`)}><Receipt className="mr-1 h-4 w-4" />Fattura</Button>
                      <Button variant="ghost" size="icon" data-testid={`ddt-edit-${d.id}`} onClick={() => nav(`/app/ddt/${d.id}`)}><Pencil className="h-4 w-4" /></Button>
                      {!isAgent(user) && <ConfirmDelete testid={`ddt-delete-${d.id}`} onConfirm={() => del(d.id)} />}
                    </>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      <SignatureDialog ddt={firma} onClose={() => setFirma(null)} onSaved={load} />
    </div>
  );
}
